import { useEffect, useMemo, useState } from 'react'
import {
  H5,
  Paragraph,
  Separator,
  YStack,
  XStack,
  useTheme,
  useEvent,
  View,
  SizableText,
} from 'tamagui'
import { InputLabel } from '@/src/components/ui/forms/inputs/inputsParts'
import { Chip } from '@/src/design-system/chip'
import { InputPhone } from '@/src/common/components/Input'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { GET_USER_INFO } from '@/src/common/gql/queries/user'
import { useUserStore } from '@/src/common/store/useUserStore'
import { parsePhoneNumberWithError } from 'libphonenumber-js'
import { parsePhoneValue } from '@/src/common/utils/input'
import { z } from 'zod/v4'
import { TouchableOpacity } from 'react-native'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import { formatPhoneNumberForBackend } from '@/src/common/utils/formatPhoneNumberForBackend'
import { Button } from '@/src/common/components/Button'
import * as E2E from '../../e2e'

const FormSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, 'Phone Number is required')
    .regex(/^\(\d{3}\) \d{3}-\d{4}$/, 'Phone Number is not valid.'),
  timeOfDay: z.enum([
    '8am - 10am',
    '10am - 12pm',
    '12pm - 2pm',
    '2pm - 4pm',
    '4pm - 6pm',
    '6pm - 8pm',
  ]),
})

export type FormValues = z.infer<typeof FormSchema>

export function ScheduleCallForm({
  onFormSubmit,
}: {
  onFormSubmit: (values: FormValues) => Promise<void>
}) {
  const theme = useTheme()
  const primaryColor = theme.primary.val
  const [timeOfDay, setTimeOfDay] = useState<FormValues['timeOfDay'] | null>(null)
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { data: userInfo } = useLoggedInQuery(GET_USER_INFO)

  // Local State
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { countryCode } = useUserStore()

  const phoneNumberInDatabase = userInfo?.getUserInfo.phoneNumber

  const phoneNumberWithoutCountryCode = useMemo(() => {
    try {
      if (phoneNumberInDatabase == null) {
        return null
      }
      return parsePhoneNumberWithError(phoneNumberInDatabase, {
        defaultCountry: 'US',
      }).formatNational()
    } catch {
      return null
    }
  }, [phoneNumberInDatabase])

  const formatPhoneNumberToDisplayToUser = useEvent((phoneNumber: string) => {
    try {
      const parsedPhoneNumber = parsePhoneValue(phoneNumber)
      setPhoneNumber(parsedPhoneNumber)
      const withoutCountryCode = parsePhoneNumberWithError(phoneNumber, {
        defaultCountry: 'US',
      }).formatNational()
      setPhoneNumber(withoutCountryCode)
      return withoutCountryCode
    } catch {
      return phoneNumber
    }
  })

  const handlePhoneBlur = useEvent(() => {
    formatPhoneNumberToDisplayToUser(phoneNumber)
  })

  const handleSubmit = useEvent(async () => {
    if (!timeOfDay) {
      return
    }

    setIsSubmitting(true)
    await onFormSubmit({
      phoneNumber: formatPhoneNumberForBackend(parsePhoneValue(phoneNumber), countryCode),
      timeOfDay,
    })
    setIsSubmitting(false)
  })

  useEffect(() => {
    if (phoneNumberWithoutCountryCode) {
      formatPhoneNumberToDisplayToUser(phoneNumberWithoutCountryCode)
    }
  }, [phoneNumberWithoutCountryCode, formatPhoneNumberToDisplayToUser])

  const isFormValid = FormSchema.safeParse({ phoneNumber, timeOfDay }).success

  return (
    <YStack gap="$2">
      <H5>Learn more about Mesa Mortgage</H5>
      <Paragraph>
        Earn 1,000 Mesa Points when you chat with a Mesa Loan Expert to learn more.
      </Paragraph>
      <Separator />
      <YStack>
        <SizableText size="$4">Enter your phone number:</SizableText>
        <SizableText size="$3" color="$textSubtle">
          (we will only use this to contact you for this call)
        </SizableText>
      </YStack>
      <InputPhone
        placeholder="Phone number"
        inputValue={{ phoneNumber, countryCode }}
        onChangeValue={({ phoneNumber }) => setPhoneNumber(phoneNumber)}
        onBlur={handlePhoneBlur}
        keyboardType="phone-pad"
        containerStyle={{ paddingHorizontal: 0 }}
        maxLength={14}
        returnKeyType="done"
      />
      <InputLabel>Select a time that works for you:</InputLabel>
      <XStack flexWrap="wrap" gap="$3">
        {FormSchema.shape.timeOfDay.options.map((option) => (
          <Chip
            key={option}
            label={option}
            containerStyle={{
              marginRight: 0,
              borderColor: primaryColor,
              backgroundColor: timeOfDay === option ? primaryColor : 'transparent',
            }}
            onPress={() => setTimeOfDay(option)}
            selected={timeOfDay === option}
          />
        ))}
      </XStack>
      <View height="$2" />
      <SizableText size="$3" color="$textSubtle">
        By scheduling a call, you agree to the{' '}
        <TouchableOpacity
          style={{ marginTop: -3 }}
          onPress={() =>
            void openInAppBrowser(
              'https://www.mesamember.com/legal/privacy-policy',
              'Mesa Privacy Policy',
            )
          }
        >
          <SizableText size="$3" color="$textSubtle" textDecorationLine="underline">
            Mesa Privacy Policy
          </SizableText>
        </TouchableOpacity>
        .
      </SizableText>
      <Button
        title="Schedule Call"
        disabled={!isFormValid || isSubmitting}
        onPress={() => void handleSubmit()}
        loading={isSubmitting}
        testID={E2E.Config.Ids.Press.ScheduleCallSubmit}
      />
    </YStack>
  )
}
